// Contenu du tour guidé du menu v2.
// Keyé sur les `key` de menuItemsV2 → reste synchronisé avec la sidebar et
// filtrable par habilitation (mêmes droits que le menu). Pour chaque groupe :
//   utilite   : à quoi sert le menu
//   nouveaute : ce qui change par rapport à l'ancien menu (v1)
//   items     : { <key du sous-menu> : phrase d'explication }

export const menuTourIntro = {
    welcomeTitle: "Bienvenue dans le nouveau menu",
    welcome:
        "Le menu est réorganisé par activité : production, attestations, caisse, reversements, commissions, " +
        "réseau, sinistres, opérations, comptabilité, statistiques, paramétrage et sécurité. Plusieurs pages " +
        "ont été modernisées ou ajoutées. Ce tour affiche uniquement les menus et sous-menus autorisés par " +
        "vos habilitations.",

    switchTitle: "Activer / quitter le menu v2",
    switch:
        "Le nouveau menu est activé par défaut. L'interrupteur « Menu v2 » permet de revenir à l'ancien menu " +
        "si nécessaire, puis de réactiver la v2 à tout moment : rien n'est perdu, les deux menus pointent vers les mêmes données.",
    switchV1Hint:
        "Activez l'interrupteur « Menu v2 » ci-dessus, puis relancez ce tour (bouton « ? ») pour découvrir " +
        "le détail de chaque menu.",

    replayHint: "Vous pourrez rejouer ce tour à tout moment via le bouton « ? » en haut de la barre latérale.",
};

export const menuTourContent = {
    DASHBOARD: {
        utilite: "Votre point d'entrée après connexion. Il sert à retrouver rapidement les indicateurs et les raccourcis liés à votre activité.",
        nouveaute: "Le menu v2 garde le tableau de bord en première position pour revenir facilement à la vue d'ensemble.",
        items: {},
    },

    PRODUCTION: {
        utilite: "Tout ce qui concerne la souscription et le suivi des contrats : création, contrats émis, propositions en cours, assurés et états.",
        nouveaute:
            "« Tous les contrats » ouvre la page Production v3 : elle se concentre sur les contrats et exclut les propositions. " +
            "Les propositions ont maintenant leur propre entrée pour éviter de mélanger les dossiers à finaliser avec les contrats validés.",
        items: {
            "v2-prod-new": "Démarrer une nouvelle souscription avec le nouveau formulaire de contrat.",
            "v2-prod-all": "Rechercher, filtrer et consulter les contrats émis dans la vue v3, sans les propositions.",
            "v2-prod-prop": "Reprendre une proposition non finalisée, la modifier, la valider ou la mettre en corbeille selon vos droits.",
            "v2-prod-assures": "Consulter le répertoire des assurés et accéder à leurs informations utiles.",
            "v2-prod-etats": "Produire les états de production par période, agence, compagnie ou critères disponibles.",
        },
    },

    ATTESTATION: {
        utilite: "Suivre le cycle des attestations : commandes, lots reçus, stock disponible et attestations dispatchées.",
        nouveaute: "Les actions de commande et de consultation sont séparées. Le lien s'adapte au type d'organisme de l'utilisateur.",
        items: {
            "v2-att-cmd": "Passer ou suivre une commande d'attestations selon votre organisme.",
            "v2-att-consult": "Consulter les lots, le stock et les attestations mises à disposition.",
        },
    },

    CAISSE: {
        utilite: "Traiter les primes à encaisser, consulter les encaissements déjà effectués et gérer les flux de caisse.",
        nouveaute:
            "La caisse est découpée par tâche : les primes en attente sont dans un écran v2 dédié, les encaissements effectués " +
            "dans un autre écran v2, et les transactions mobile money sont regroupées dans la même zone.",
        items: {
            "v2-caisse-encaisser": "Afficher uniquement les règlements en attente et procéder à l'encaissement.",
            "v2-caisse-enc": "Consulter les encaissements réalisés, avec les filtres v2 et les actions d'annulation autorisées.",
            "v2-caisse-mobile": "Contrôler les paiements mobile money reçus et leur rattachement.",
            "v2-caisse-bord-revers": "Encaisser un bordereau de reversement reçu en compagnie.",
        },
    },

    REVERSEMENT: {
        utilite: "Préparer, enregistrer et suivre les reversements de primes vers la compagnie.",
        nouveaute: "Le reversement de primes est séparé de la caisse pour distinguer clairement l'encaissement client du reversement compagnie.",
        items: {
            "v2-rev-create": "Créer un bordereau de reversement à partir des primes encaissées.",
            "v2-rev-vt": "Enregistrer un reversement bancaire pour les flux STCA / GUM.",
            "v2-rev-list": "Consulter l'historique et l'état des bordereaux de reversement.",
        },
    },

    COMMISSION: {
        utilite: "Gérer les commissions liées à la production : génération, bordereaux et suivi de vos montants.",
        nouveaute: "Les commissions ont leur propre espace pour ne plus les confondre avec les reversements de primes.",
        items: {
            "v2-com-gen": "Générer les bordereaux de commissions selon les règles applicables.",
            "v2-com-bord": "Consulter les bordereaux de commissions déjà produits.",
            "v2-com-mine": "Suivre vos commissions agence ou courtier selon votre profil.",
        },
    },

    RESEAU: {
        utilite: "Piloter le réseau de distribution : agences, courtiers, intermédiaires, accréditations et taux de commissions.",
        nouveaute:
            "Nouveau module qui regroupe des pages auparavant dispersées : création et suivi d'agence, accréditation courtier, " +
            "suivi courtier, suivi intermédiaire et taux de commissions v2.",
        items: {
            "v2-reseau-agence-new": "Créer ou administrer les agences depuis l'espace compagnie.",
            "v2-reseau-agence-suivi": "Analyser la production, les encaissements, les commissions et les reversements par agence.",
            "v2-reseau-courtier-new": "Valider ou suivre les demandes d'accréditation des courtiers.",
            "v2-reseau-courtier-suivi": "Suivre l'activité des courtiers : production, commissions et reversements.",
            "v2-reseau-intermediaires": "Comparer l'activité des intermédiaires et accéder à leurs données de suivi.",
            "v2-reseau-taux": "Paramétrer les taux de commissions dans l'écran v2 modernisé.",
        },
    },

    SINISTRES: {
        utilite: "Gérer tout le cycle sinistre : déclaration, instruction, évaluations, SAP, règlements et états.",
        nouveaute:
            "Les dossiers sinistres sont désormais orientés par tâche. Les sinistres, évaluations et règlements disposent " +
            "de listings v2 plus lisibles, et les SAP ont une entrée dédiée.",
        items: {
            "v2-sin-decl": "Déclarer un nouveau sinistre et ouvrir le dossier correspondant.",
            "v2-sin-list": "Instruire les dossiers sinistres, rechercher les dossiers et accéder aux détails.",
            "v2-sin-evals": "Lister et valider les évaluations avec l'écran v2 et ses filtres de dates.",
            "v2-sin-sap": "Consulter les sommes à payer et les engagements liés aux sinistres.",
            "v2-sin-regl": "Lister et valider les règlements, y compris les règlements experts, entités, conseils juridiques et établissements.",
            "v2-sin-etats": "Produire les états sinistres : payés, honoraires, bons à payer, ratio S/P et autres restitutions.",
        },
    },

    OPERATIONS: {
        utilite: "Consulter les opérations de cession et de rétrocession.",
        nouveaute: "La cession et la rétrocession sont regroupées dans un espace unique.",
        items: {
            "v2-op-cession": "Consulter les cessions et leurs informations de suivi.",
            "v2-op-retro": "Consulter les rétrocessions et les documents associés.",
        },
    },

    COMPTABILITE: {
        utilite: "Consulter et contrôler les données comptables : comptes, écritures, balance et fiscalité.",
        nouveaute: "La fiscalité apparaît comme nouveau sous-espace de la comptabilité.",
        items: {
            "v2-compta-comptes": "Consulter le plan comptable et les comptes.",
            "v2-compta-livre": "Consulter le grand livre et les écritures comptables.",
            "v2-compta-balance": "Éditer ou contrôler la balance comptable.",
            "v2-compta-fiscalite": "Accéder aux déclaratifs et retenues à reverser.",
            "v2-fisc-tca": "Suivre la TCA à reverser.",
            "v2-fisc-honoraires": "Suivre les retenues sur honoraires.",
            "v2-fisc-commissions": "Suivre les retenues sur commissions.",
        },
    },

    STATS: {
        utilite: "Accéder aux états et statistiques de pilotage pour la production et les sinistres.",
        nouveaute: "Les états les plus utilisés sont réunis dans un espace de consultation rapide.",
        items: {
            "v2-stats-prod": "Consulter les états de production.",
            "v2-stats-agence": "Analyser la production par agence.",
            "v2-stats-sin": "Consulter les états et statistiques sinistres.",
        },
    },

    PARAMETRES: {
        utilite: "Configurer les référentiels et règles de l'application : tarification, sinistres, comptabilité, compagnie et paramètres divers.",
        nouveaute:
            "La Tarification v2 est allégée. Les intermédiaires, courtiers et taux de commissions sont déplacés dans le module Réseau de distribution.",
        items: {
            "v2-param-tarif": "Configurer la tarification dans le hub v2 : garanties, catégories, primes, engins et règles associées.",
            "v2-param-sin": "Configurer les référentiels sinistres : causes, préjudices, entités, conseils juridiques et paramètres de règlement.",
            "v2-param-compta": "Paramétrer les éléments comptables utilisés par les écritures.",
            "v2-param-compagnie": "Gérer les informations de la compagnie, ses agences et ses organismes.",
            "v2-param-autres": "Accéder aux paramètres divers qui ne relèvent pas des autres familles.",
        },
    },

    DROITS: {
        utilite: "Administrer les accès : utilisateurs, profils, habilitations, journal d'activité et synchronisations.",
        nouveaute: "Les fonctions de sécurité et de traçabilité sont regroupées dans un seul espace.",
        items: {
            "v2-droits-users": "Créer, modifier et suivre les utilisateurs.",
            "v2-droits-profils": "Créer et organiser les profils d'accès.",
            "v2-droits-hab": "Gérer les habilitations qui déterminent les menus et actions visibles.",
            "v2-droits-journal": "Consulter le journal des activités pour la traçabilité.",
            "v2-droits-sync": "Suivre les synchronisations et leurs éventuelles erreurs.",
        },
    },

    SUPPORT: {
        utilite: "Accéder au support OssanAssur pour l'assistance et le suivi des demandes.",
        nouveaute: "Le lien support reste disponible directement depuis la barre latérale.",
        items: {},
    },
};
